import { useState, type FormEvent } from 'react';
import { useTranslations } from '@vp/om-translations';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  FormError,
  FormField,
  StandardForm,
  FormLabel,
  SearchInput,
  FormInputGroup,
} from '@vp/swan';
import { useSiteData } from '../../hooks/useSiteData';
import {
  trackFindOrderAttempted,
  trackFindOrderAttemptFailed,
  trackFindOrderSuccess,
} from '../../apis/newrelic/newRelicTrackingApi';
import { getOrderDetails } from '../../apis/bopApi';
import { useShopperId } from '../../hooks/useShopperId';

/*
  Collect and validate order info required to fetch an order
*/

export const FindOrder = () => {
  const shopperId = useShopperId();

  const [isOrderNumberValid, setIsOrderNumberValid] = useState(true);
  const [orderFetchError, setOrderFetchError] = useState(false);
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [findOrderMessages = {}, anonymousOrderDetailsMessages = {}] =
    useTranslations([
      'findOrderFromOrderHistoryMessages',
      'anonymousOrderDetailsMessages',
    ]);
  const {
    externalAppNavigation: { orderDetails: orderDetailsUrl },
    siteName,
    tenantEnvironment,
  } = useSiteData();

  const { mutate: fetchOrder } = useMutation({
    mutationFn: async (orderNumber: string) => {
      try {
        const order = await getOrderDetails({
          orderNumber,
          shopperId,
          environment: tenantEnvironment,
        });
        // if orderFetch succeeded, navigate to OD for search orderNum
        trackFindOrderSuccess({ siteName, orderNumber });
        window.location.href = `${orderDetailsUrl}?orderId=${order.orderNumber}`;
      } catch (error) {
        // Maintain separate error state so error not shown when user changes input
        setOrderFetchError(true);
        const findOrderFailureData = { siteName, orderNumber };
        if (error instanceof Error) {
          trackFindOrderAttemptFailed(error, findOrderFailureData);
        }
      }
    },
  });

  const clearErrors = () => {
    setIsOrderNumberValid(true);
    setOrderFetchError(false);
  };

  // Clear input value and any error messages
  const clearInput = () => {
    setOrderNumberInput('');
    clearErrors();
  };

  // Submit the form as long as the input is not empty
  const submitForm = (event: FormEvent) => {
    event.preventDefault();
    // Any leading whitespace (spaces or tabs) in front of the order number will fail the search,
    // so we use `trim()` to prevent the user from typing any whitespace in the input field.
    // It also removes leading or trailing white space that is inputted via copy/paste,
    // which is especially helpful in the scenario where they copy/paste it from a confirmation email!
    const input = orderNumberInput.trim();
    if (input.length === 0) {
      setIsOrderNumberValid(false);
      return;
    }

    trackFindOrderAttempted({
      siteName,
      orderNumber: orderNumberInput,
    });

    fetchOrder(orderNumberInput);
  };

  return (
    <StandardForm onSubmit={submitForm}>
      <FormField>
        <FormInputGroup>
          <FormLabel visuallyHidden>
            {anonymousOrderDetailsMessages.formTitle}
          </FormLabel>
          <SearchInput
            id="order-number-input"
            placeholder={findOrderMessages.searchForOrderNumber}
            accessibleTextForClearButton={findOrderMessages.clear}
            accessibleTextForSearchButton={
              findOrderMessages.searchForOrderNumber
            }
            value={orderNumberInput}
            onChange={(e) => {
              setOrderNumberInput(e.target.value.trim());
              clearErrors();
            }}
            onClear={clearInput}
            skin={isOrderNumberValid ? 'standard' : 'error'}
          />
          <Box role="status" aria-live="polite">
            {!isOrderNumberValid && (
              <FormError>
                {findOrderMessages.orderNumberRequiredError}
              </FormError>
            )}
            {orderFetchError && (
              <FormError>{findOrderMessages.orderNotFoundError}</FormError>
            )}
          </Box>
        </FormInputGroup>
      </FormField>
    </StandardForm>
  );
};
